"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClientWebRTC = void 0;
const tslib_1 = require("tslib");
const core_1 = require("@apollo/client/core");
const utilities_1 = require("@apollo/client/utilities");
const ws_1 = require("@apollo/client/link/ws");
const error_1 = require("@apollo/client/link/error");
const simple_peer_1 = tslib_1.__importDefault(require("simple-peer"));
const events_1 = require("events");
const common_1 = require("@gameglass/common");
const types_1 = require("./types");
const graphql_1 = require("./utils/graphql");
const debugger_1 = require("./utils/debugger");
const uuid_1 = require("./utils/uuid");
const clientpeer_1 = require("./clientpeer");
const axios_1 = tslib_1.__importDefault(require("axios"));
const websocket_1 = require("./utils/websocket");
let instance;
let httpLink;
let wsLink;
let splitLink;
let errorLink;
let client;
class ClientWebRTC {
    constructor({ config = {}, debug = false, wrtc, httpUrl = "https://api.gameglass.gg/graphql", webSocketUrl = "wss://api.gameglass.gg/graphql", tokenUrl = "https://api.gameglass.gg/config/webrtc" }) {
        this.initializeApollo = () => {
            httpLink = new core_1.HttpLink({
                uri: this.httpUrl,
                headers: this.generateHeaders()
            });
            wsLink = new ws_1.WebSocketLink({
                uri: this.webSocketUrl,
                options: {
                    timeout: 60000,
                    minTimeout: 5000,
                    reconnect: true,
                    connectionParams: {
                        headers: this.generateHeaders()
                    }
                },
                webSocketImpl: websocket_1.websocket
            });
            splitLink = (0, core_1.split)(({ query }) => {
                const definition = (0, utilities_1.getMainDefinition)(query);
                return (definition.kind === "OperationDefinition" &&
                    definition.operation === "subscription");
            }, wsLink, httpLink);
            errorLink = (0, error_1.onError)(({ graphQLErrors, networkError, response }) => {
                if (graphQLErrors) {
                    graphQLErrors.map((graphQLError) => {
                        const { message, locations, path } = graphQLError;
                        this.debugger.warn({
                            title: types_1.LogTypes.graphqlError,
                            args: {
                                message,
                                locations,
                                path
                            }
                        });
                        this._events[types_1.PeerEvents.error].forEach((cb) => {
                            cb({ error: graphQLError });
                        });
                    });
                }
                if (networkError) {
                    this.debugger.warn({
                        title: types_1.LogTypes.networkError,
                        args: networkError
                    });
                    this._events[types_1.PeerEvents.error].forEach((cb) => {
                        cb({ error: networkError });
                    });
                }
                if (response) {
                    response.errors = undefined;
                }
            });
            client = new core_1.ApolloClient({
                uri: this.httpUrl,
                cache: new core_1.InMemoryCache(),
                link: errorLink.concat(splitLink)
            });
        };
        this._token = "";
        this._userID = "";
        this.generateHeaders = () => {
            const headers = {
                "content-type": "application/json"
            };
            if (this.token) {
                headers.Authorization = `Token ${this.token}`;
            }
            if (this.userID) {
                headers["X-GG-User-Id"] = this.userID;
            }
            return headers;
        };
        this.source = "client";
        this.config = {
            iceServers: [
                {
                    "urls": "stun:global.stun.twilio.com:3478"
                }
            ],
            iceCandidatePoolSize: 5
        };
        this.peers = [];
        this.trickle = false;
        this.eventEmitter = new events_1.EventEmitter();
        this.sendWhenConnectedQueue = new common_1.Queue();
        this.subscription = null;
        this.unsubscribe = () => tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            (_a = this === null || this === void 0 ? void 0 : this.subscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
            this.subscription = null;
            ((_b = wsLink === null || wsLink === void 0 ? void 0 : wsLink.subscriptionClient) === null || _b === void 0 ? void 0 : _b.status) === websocket_1.websocket.OPEN && ((_c = wsLink === null || wsLink === void 0 ? void 0 : wsLink.subscriptionClient) === null || _c === void 0 ? void 0 : _c.close());
        });
        this.subscribe = () => tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                this.subscription = client
                    .subscribe({
                    query: graphql_1.SIGNAL_SUBSCRIPTION
                })
                    .subscribe(({ data }) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                    const signalReceived = data[graphql_1.SIGNAL_RECEIVED_TYPE];
                    if (signalReceived.source.toLowerCase() === this.source) {
                        return;
                    }
                    clearInterval(this.pingInterval);
                    if (!data || !signalReceived) {
                        return this.debugger.warn({
                            title: types_1.LogTypes.signalReceivedError,
                            args: signalReceived || data
                        });
                    }
                    const { uuid, sdp } = signalReceived;
                    const thisUUID = this.peerId;
                    if (uuid !== thisUUID && sdp !== "noop")
                        return;
                    const foundPeer = this.getPeer(thisUUID);
                    if (foundPeer) {
                        this.debugger.debug({
                            title: types_1.LogTypes.signalReceived,
                            args: signalReceived
                        });
                    }
                    if (!(foundPeer === null || foundPeer === void 0 ? void 0 : foundPeer.lastValidPeer)) {
                        foundPeer === null || foundPeer === void 0 ? void 0 : foundPeer.addPeer(this.createPeer({ uuid: thisUUID }));
                    }
                    foundPeer && foundPeer.signal(signalReceived);
                }));
            }
            catch (error) {
                this.debugger.error({
                    title: types_1.LogTypes.subscriptionError,
                    args: error
                });
                this._events[types_1.PeerEvents.error].forEach((cb) => cb({ error }));
            }
            return this.subscription;
        });
        this.mutate = ({ sdp, uuid, isCandidate }) => {
            if (sdp === null || sdp === "") {
                return;
            }
            const variables = {
                sdp,
                uuid,
                event: isCandidate ? "CANDIDATE" : "ANSWER",
                source: this.source.toUpperCase()
            };
            return client
                .mutate({
                variables,
                mutation: graphql_1.SIGNAL_MUTATION
            })
                .then((mutation) => {
                this.debugger.debug({
                    title: types_1.LogTypes.signalSent,
                    args: mutation
                });
                return mutation;
            })
                .catch((error) => {
                this.debugger.error({
                    title: types_1.LogTypes.signalSentError,
                    args: error
                });
                this._events[types_1.PeerEvents.error].forEach((cb) => cb({ error }));
                return error;
            });
        };
        this.noopCandidateMutation = () => {
            void this.mutate({
                uuid: this.peerId,
                sdp: "noop",
                isCandidate: true
            });
        };
        this.start = () => tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.stop();
            if (!this.token) {
                throw new Error("Missing token.");
            }
            yield this.getRTCConfig();
            this.createConnection({ uuid: this.peerId });
            this.initializeApollo();
            this.attachCustomEvents();
            if (!this.subscription || !this.subscription.closed ||
                !this.subscription.unsubscribe) {
                void this.subscribe();
            }
            const wsLinkAsAny = wsLink;
            wsLinkAsAny.subscriptionClient.onConnected(() => {
                this.debugger.info({
                    title: types_1.LogTypes.wsConnected,
                    args: wsLink
                });
                this.startNoopMutation();
                this.ping();
            });
            wsLinkAsAny.subscriptionClient.onDisconnected(() => {
                this.debugger.warn({
                    title: types_1.LogTypes.wsDisconnected,
                    args: wsLink
                });
                clearInterval(this.pingInterval);
            });
            wsLinkAsAny.subscriptionClient.onReconnecting(() => {
                this.debugger.warn({
                    title: types_1.LogTypes.wsReconnecting,
                    args: wsLink
                });
            });
            wsLinkAsAny.subscriptionClient.onReconnected(() => {
                this.debugger.warn({
                    title: types_1.LogTypes.wsReconnected,
                    args: wsLink
                });
                this.ping();
                if (this.subscription && this.subscription.closed) {
                    void this.subscribe();
                }
            });
            wsLinkAsAny.subscriptionClient.onError(() => {
                this.debugger.error({
                    title: types_1.LogTypes.wsError,
                    args: wsLink
                });
            });
        });
        this.createConnection = ({ uuid }) => {
            const _uuid = uuid || (0, uuid_1.uuidv4)();
            this.removePeer(uuid);
            const ggpeer = new clientpeer_1.ClientPeer(this.createPeer({ uuid: _uuid }), this.noopReply, this.wrtc, this.debug);
            this.addPeer(ggpeer);
            return ggpeer.peer;
        };
        this.createPeer = ({ uuid }) => {
            const config = {
                iceCompleteTimeout: 15000,
                initiator: false,
                trickle: this.trickle,
                objectMode: true,
                config: this.config,
                wrtc: this.wrtc
            };
            const peer = new simple_peer_1.default(config);
            peer.uuid = uuid;
            this.attachEventsToPeer(peer);
            return peer;
        };
        this.setTrickle = (trickle) => {
            this.trickle = trickle;
        };
        this.sendTo = (peer, message) => {
            var _a;
            (_a = this.getPeer(peer.uuid || "")) === null || _a === void 0 ? void 0 : _a.send(message);
        };
        this.sendWhenConnected = (message) => {
            if (this.peers.every((p) => p.connected)) {
                this.send(message);
            }
            else {
                void this.sendWhenConnectedQueue.push(() => this.send(message), false);
            }
        };
        this.send = (message) => {
            this.peers.forEach(p => p.send(message));
        };
        this.stop = (cleanup) => {
            cleanup && this.off();
            client && client.stop();
            clearInterval(this.RTCConfigTimer);
            void this.unsubscribe();
            this.removeAllPeers();
        };
        this.startNoopMutation = () => {
            this.noopCandidateMutation();
        };
        this.getRTCConfig = () => tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                const twilioRTCConfig = yield axios_1.default.get(this.tokenUrl, {
                    headers: this.generateHeaders()
                });
                this.config = Object.assign(Object.assign({}, this.config), twilioRTCConfig.data);
                return twilioRTCConfig.data;
            }
            catch (error) {
                this.debugger.error({
                    title: error.name,
                    args: error
                });
            }
            return this.config;
        });
        this.noopReply = () => {
            this.noopCandidateMutation();
        };
        this.attachCustomEvents = () => {
            this.eventEmitter.removeAllListeners();
            const customEvents = Object.keys(types_1.CustomEvents);
            customEvents.forEach((eventName) => {
                this.eventEmitter.on(eventName, (data) => {
                    if (eventName !== types_1.CustomEvents.debugger) {
                        this.debugger.debug({
                            title: eventName,
                            args: data
                        });
                    }
                    this._events[eventName].forEach((cb) => cb({ data }));
                });
            });
        };
        this._events = {
            [types_1.PeerEvents.signal]: [],
            [types_1.PeerEvents.connect]: [],
            [types_1.PeerEvents.data]: [],
            [types_1.PeerEvents.error]: [],
            [types_1.PeerEvents.close]: [],
            [types_1.CustomEvents.peersChange]: [],
            [types_1.CustomEvents.debugger]: []
        };
        this.on = (event, callback) => {
            this._events[event].push(callback);
            return () => this.off(event, callback);
        };
        this.off = (event, callback) => {
            if (event) {
                if (callback) {
                    this._events[event] = this._events[event].filter((_callback) => _callback !== callback);
                }
                else {
                    this._events[event] = [];
                }
            }
            else {
                Object.keys(this._events).forEach((key) => {
                    this._events[key] = [];
                });
            }
        };
        this.attachEventsToPeer = (peer) => {
            let ready = null;
            peer.ready = new Promise((resolve) => (ready = resolve));
            peer.on(types_1.PeerEvents.signal, (data) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                this.debugger.debug({
                    title: types_1.LogTypes.onPeerSignal,
                    args: { data, peer }
                });
                const sendMessageToPeer = (peer) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                    var _a;
                    const sdp = data === null || data === void 0 ? void 0 : data.sdp;
                    const candidate = (_a = data === null || data === void 0 ? void 0 : data.candidate) === null || _a === void 0 ? void 0 : _a.candidate;
                    yield this.mutate({
                        sdp: (sdp || candidate),
                        uuid: peer.uuid,
                        isCandidate: !!candidate
                    });
                    if (sdp)
                        ready === null || ready === void 0 ? void 0 : ready(true);
                    this._events[types_1.PeerEvents.signal].forEach((callback) => {
                        callback({ data, peer });
                    });
                });
                yield sendMessageToPeer(peer);
            }));
            peer.on(types_1.PeerEvents.connect, () => tslib_1.__awaiter(this, void 0, void 0, function* () {
                var _b;
                this.debugger.debug({
                    title: types_1.LogTypes.onPeerConnect,
                    args: peer
                });
                if ((_b = this.subscription) === null || _b === void 0 ? void 0 : _b.closed)
                    return;
                if (peer.connected) {
                    void this.unsubscribe();
                }
            }));
            peer.once(types_1.PeerEvents.connect, () => {
                this._events[types_1.PeerEvents.connect].forEach((cb) => cb({ peer }));
                void this.sendWhenConnectedQueue.startProcessing();
            });
            peer.on(types_1.PeerEvents.data, (_data) => {
                let data;
                try {
                    data = JSON.parse(_data);
                }
                catch (error) {
                    data = _data;
                }
                this.debugger.debug({
                    title: types_1.LogTypes.onPeerMessage,
                    args: { data, peer }
                });
                this._events[types_1.PeerEvents.data].forEach((cb) => cb({ data, peer }));
            });
            const onError = (error) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                var _c;
                this.debugger.warn({
                    title: types_1.LogTypes.onPeerError,
                    args: { error, peer }
                });
                if ((_c = this.getPeer(peer.uuid || "")) === null || _c === void 0 ? void 0 : _c.connected) {
                    return;
                }
                this._events[types_1.PeerEvents.error].forEach((cb) => cb({ error, peer }));
            });
            const onClose = (reason) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                this.debugger.info({
                    title: types_1.LogTypes.onPeerClose,
                    args: { reason, peer }
                });
                if (this.peer.lastValidPeer)
                    return;
                if (!this.subscription || !this.subscription.closed || !this.subscription.unsubscribe) {
                    yield this.subscribe();
                }
                this.removePeer(this.peerId);
                this.peerId = (0, uuid_1.uuidv4)();
                this.createConnection({ uuid: this.peerId });
                this.startNoopMutation();
                this._events[types_1.PeerEvents.close].forEach((cb) => cb({ error: reason, peer }));
            });
            peer.on(types_1.PeerEvents.error, onError);
            peer.on(types_1.PeerEvents.close, onClose);
        };
        this.generateUUID = () => `${this.source}-${(0, uuid_1.uuidv4)()}`;
        if (instance) {
            instance.stop();
        }
        else {
            instance = this;
            if (typeof window !== "undefined") {
                window.addEventListener("beforeunload", () => {
                    instance.stop();
                });
            }
        }
        this.peerId = (0, uuid_1.uuidv4)();
        this.wrtc = wrtc;
        this.pingInterval = 0;
        this.config = Object.assign(Object.assign({}, this.config), config);
        this.peers = [];
        this.debug = debug;
        this.debugger = new debugger_1.Debugger(this.debug, this.eventEmitter);
        this.httpUrl = httpUrl;
        this.webSocketUrl = webSocketUrl;
        this.tokenUrl = tokenUrl;
        return instance;
    }
    get token() {
        return this._token || (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
    }
    set token(token) {
        this._token = token;
    }
    get userID() {
        return this._userID || (typeof sessionStorage !== "undefined" && sessionStorage.getItem("userID")) || "";
    }
    set userID(userID) {
        this._userID = userID;
    }
    get client() {
        return client;
    }
    get peer() {
        return this.peers[this.peers.length - 1];
    }
    getPeer(uuid) {
        this.debugger.debug({ title: `getPeer`, args: uuid });
        return this.peers.find(p => p.uuid === uuid);
    }
    addPeer(peer) {
        this.debugger.debug({ title: `Add peer`, args: peer.uuid });
        if (this.peers.length > 0) {
            throw new Error("too many peers added");
        }
        this.peers = [peer];
        this.eventEmitter.emit(types_1.CustomEvents.peersChange, this.peers);
    }
    removePeer(uuid) {
        if (!uuid) {
            return;
        }
        for (let i = 0, len = this.peers.length; i < len; i++) {
            const peer = this.peers[i];
            if (peer.uuid === uuid) {
                this.debugger.debug({ title: `Remove peer`, args: uuid });
                this.peers.splice(i, 1);
                this.eventEmitter.emit(types_1.CustomEvents.peersChange, this.peers);
                peer.removePeers();
                return peer;
            }
        }
        return;
    }
    removeAllPeers() {
        this.debugger.info({ title: `Remove ALL peers` });
        this.peers.forEach(p => p.removePeers());
        this.peers = [];
    }
    get peersConnected() {
        return this.peers.some(p => p.connected);
    }
    ping() {
        clearInterval(this.pingInterval);
        this.pingInterval = setInterval(this.noopCandidateMutation.bind(this), 60000, null);
    }
}
exports.ClientWebRTC = ClientWebRTC;
ClientWebRTC.generateId = () => (0, uuid_1.uuidv4)();
//# sourceMappingURL=client.js.map